import deserialize from 'app/utils/deserialize'
import { fetch, create, update, remove } from 'app/utils/data'

// Stable error codes returned by the v2 plan-override API.
// See contracts/admin-plan-overrides.openapi.yaml.
const STABLE_ERROR_CODES = ['subscription_inactive', 'legacy_plan_uncapped']

/**
 * Normalizes any error from the plan-overrides endpoints into a shape the
 * modal can consume:
 *
 *   { code: 'subscription_inactive' | 'legacy_plan_uncapped' }     // 403
 *   { fieldErrors: [{ field, detail }] }                            // 400 (JSON:API errors)
 *   { message: '...' }                                              // anything else
 */
function normalizeError(error) {
  if (!error || !error.response) {
    return { message: (error && error.message) || 'Network error' }
  }

  const { status, data } = error.response

  if (status === 403 && data && STABLE_ERROR_CODES.includes(data.error)) {
    return { code: data.error }
  }

  if (status === 400 && data && Array.isArray(data.errors)) {
    return {
      fieldErrors: data.errors.map((e) => {
        const pointer = (e.source && e.source.pointer) || ''
        const field = pointer.split('/').pop() || 'attribute'
        return { field, detail: e.detail || 'invalid' }
      })
    }
  }

  return { message: (data && data.error) || `Request failed (${status})` }
}

export default {
  namespaced: true,

  state: {
    item: null
  },

  getters: {
    currentPlanOverride: (state) => state.item,
    hasPlanOverride: (state) => !!state.item
  },

  mutations: {
    planOverrideFetched(state, { planOverride }) {
      state.item = planOverride
    },

    resetState(state) {
      state.item = null
    }
  },

  actions: {
    /**
     * Fetches the plan override for the given subscription ID.
     * If no override is found (404), resets the state and returns null.
     */
    async fetchPlanOverride({ commit }, subscriptionId) {
      try {
        const response = await fetch({ url: `/api/admin/plan-overrides/${subscriptionId}` })
        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        if (error.response && error.response.status === 404) {
          commit('resetState')
          return null
        } else {
          throw normalizeError(error)
        }
      }
    },

    /**
     * Creates a plan override with the supplied attributes (absolute values).
     *
     * Server may reply 204 No Content (FR-014a — every field collapsed to
     * baseline; no row written). In that case we clear local state to match.
     */
    async createPlanOverride({ commit }, data) {
      try {
        const response = await create({
          url: '/api/admin/plan-overrides',
          type: 'plan-override',
          data
        })

        if (response.status === 204) {
          commit('resetState')
          return null
        }

        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        throw normalizeError(error)
      }
    },

    /**
     * Updates the existing plan override with the given changes.
     *
     * Server may reply 204 No Content (FR-014a — update brought every
     * field to baseline; row was deleted). In that case we clear local
     * state to match.
     */
    async updatePlanOverride({ commit, state }, changes) {
      try {
        const overrideId = state.item && state.item.id
        if (!overrideId) {
          throw new Error('Plan override record is missing an id')
        }
        const response = await update({
          url: `/api/admin/plan-overrides/${overrideId}`,
          patch: true,
          changes
        })

        if (response.status === 204) {
          commit('resetState')
          return null
        }

        const planOverride = await deserialize(response.data)
        commit('planOverrideFetched', { planOverride })
        return planOverride
      } catch (error) {
        throw normalizeError(error)
      }
    },

    /**
     * Deletes the plan override identified by its ID. Resets local state
     * on success.
     */
    async deletePlanOverride({ commit }, overrideId) {
      try {
        const response = await remove({
          url: `/api/admin/plan-overrides/${overrideId}`
        })
        commit('resetState')
        return response
      } catch (error) {
        throw normalizeError(error)
      }
    }
  }
}
